import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { formatPrice, productImage, shopGet, shopPost } from "../../api/shopApi";

export default function ShopCheckoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id"));
  const productId = Number(params.get("product") || 0);
  const productSlug = (params.get("slug") || "").trim();
  const initQty = Math.max(1, Math.min(99, Number(params.get("qty") || 1)));

  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(initQty);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState(0);
  const [note, setNote] = useState("");
  const [form, setForm] = useState({
    recipient_name: "",
    phone: "",
    province: "",
    city: "",
    district: "",
    detail_address: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okOrder, setOkOrder] = useState(null);

  useSeo({
    title: "提交订单 - 生活好物商城",
    path: "/shop/checkout",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, {
        replace: true,
      });
      return;
    }
    if (!productId && !productSlug) {
      setError("请先选择商品");
      return;
    }
    const loadProduct = productSlug
      ? shopGet(`/products/${encodeURIComponent(productSlug)}`)
      : shopGet(`/products?page_size=48`).then((res) => {
          const found = (res.items || []).find((p) => p.id === productId);
          if (!found) throw new Error("商品不存在或已下架");
          return shopGet(`/products/${found.slug}`);
        });
    loadProduct.then(setProduct).catch((e) => setError(e.message));

    shopGet(`/addresses?user_id=${userId}`)
      .then((res) => {
        const list = res.items || [];
        setAddresses(list);
        const def = list.find((a) => a.is_default) || list[0];
        if (def) setAddressId(def.id);
      })
      .catch(() => {});
  }, [userId, productId, productSlug, navigate]);

  const subtotal = useMemo(() => {
    if (!product) return 0;
    return Number(product.price) * qty;
  }, [product, qty]);

  const saveAddressThenOrder = async () => {
    setBusy(true);
    setError("");
    try {
      let aid = addressId;
      if (!aid) {
        if (!form.recipient_name.trim() || !form.phone.trim() || !form.detail_address.trim()) {
          throw new Error("请填写收货人或选择已有地址");
        }
        const created = await shopPost("/addresses", {
          user_id: userId,
          ...form,
          is_default: addresses.length ? 0 : 1,
        });
        aid = created.id;
        setAddresses((prev) => [created, ...prev]);
        setAddressId(aid);
      }
      const idem = `u${userId}-p${productId}-q${qty}-${Date.now()}`;
      const data = await shopPost("/orders", {
        user_id: userId,
        address_id: aid,
        items: [{ product_id: productId, quantity: qty }],
        customer_note: note,
        idempotency_key: idem,
      });
      setOkOrder(data.order);
    } catch (e) {
      setError(e.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  if (okOrder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
        <Header />
        <main className="flex-1 px-4 py-16">
          <div className="max-w-lg mx-auto bg-white rounded-3xl shadow p-8 text-center space-y-4">
            <h1 className="text-2xl font-bold text-gray-800">订单已提交</h1>
            <p className="text-sm text-gray-500 leading-7">
              订单编号：{okOrder.order_no}
              <br />
              状态：{okOrder.status_label}
              <br />
              当前为人工确认，暂不在线收款。
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to={`/shop/orders/${okOrder.order_no}`}
                className="py-3 rounded-full bg-pink-500 text-white"
              >
                查看订单详情
              </Link>
              <Link to="/shop/orders" className="py-3 rounded-full border text-pink-600">
                我的订单
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-5">
          <Link to="/shop" className="text-sm text-pink-600 hover:underline">
            ← 返回商城
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">提交购买意向</h1>
          <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-900 leading-7">
            当前为人工确认订单，提交后平台将核实商品供应、价格和发货信息。订单提交不代表支付成功，具体供货及配送安排以平台确认信息为准。
          </div>
          {error ? <p className="text-rose-600 text-sm">{error}</p> : null}
          {product ? (
            <div className="bg-white rounded-2xl shadow p-5 space-y-4">
              <div className="flex gap-3">
                <img
                  src={productImage(product.main_image)}
                  alt={product.name}
                  className="w-20 h-20 rounded-xl object-cover"
                />
                <div className="flex-1">
                  <h2 className="font-semibold text-gray-800">{product.name}</h2>
                  <p className="text-rose-600 font-bold mt-1">{formatPrice(product.price)}</p>
                </div>
              </div>
              <label className="block text-sm">
                数量
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                  className="mt-1 w-24 border rounded-lg px-2 py-1.5"
                />
              </label>
              <p className="text-sm text-gray-600">
                商品金额：<span className="font-semibold text-gray-900">{formatPrice(subtotal)}</span>
              </p>
              <p className="text-xs text-gray-400">运费以平台确认后的实际安排为准（当前展示运费 ¥0.00）。</p>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-800">收货地址</h3>
                {addresses.length ? (
                  <div className="space-y-2">
                    {addresses.map((a) => (
                      <label
                        key={a.id}
                        className={`flex gap-2 p-3 rounded-xl border cursor-pointer ${
                          addressId === a.id ? "border-pink-400 bg-pink-50" : "border-gray-100"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={addressId === a.id}
                          onChange={() => setAddressId(a.id)}
                        />
                        <span className="text-sm text-gray-700">
                          {a.recipient_name} {a.phone}
                          <br />
                          {a.full_address}
                        </span>
                      </label>
                    ))}
                    <button
                      type="button"
                      className="text-sm text-pink-600"
                      onClick={() => setAddressId(0)}
                    >
                      使用新地址
                    </button>
                  </div>
                ) : null}
                {!addressId ? (
                  <div className="grid gap-2">
                    {[
                      ["recipient_name", "收货人"],
                      ["phone", "联系电话"],
                      ["province", "省"],
                      ["city", "市"],
                      ["district", "区"],
                      ["detail_address", "详细地址"],
                    ].map(([key, label]) => (
                      <input
                        key={key}
                        placeholder={label}
                        value={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                        className="border rounded-lg px-3 py-2 text-sm"
                      />
                    ))}
                  </div>
                ) : null}
              </div>

              <label className="block text-sm">
                订单备注
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  placeholder="选填，如希望发货前电话联系"
                />
              </label>

              <button
                type="button"
                disabled={busy || !product}
                onClick={saveAddressThenOrder}
                className="w-full py-3.5 rounded-full bg-pink-500 text-white font-semibold disabled:opacity-50"
              >
                {busy ? "提交中…" : "提交订单"}
              </button>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-10">加载商品信息…</p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
